import { Fragment } from "react";

import type { IntlShape } from "react-intl";

import { Divider } from "@/components/atoms/Divider";
import { PriceLineItem } from "@/components/molecules/PriceLineItem";
import { PriceSummary } from "@/components/molecules/PriceSummary/PriceSummary";

import { AddOnItemDetails } from "./AddOnItemDetails";
import { CloseListOnItemDetailsUnmount } from "./CloseListOnItemDetailsUnmount";
import { labelMessages } from "./priceSummaryLabels.messages";
import type { PriceDetailModel, PriceDetailRow } from "./utils/types";
import { formatLabelRef } from "./utils/ui/formatLabelRef";

type AddOnsDetailContentProps = {
  currency: string;
  intl: IntlShape;
  model: PriceDetailModel;
  /**
   * Closes the parent Add-ons list modal when an item-details modal unmounts
   * (no back stack). Pass the `setOpen` from PriceLineItem `renderContent`.
   */
  onListOpenChange?: (open: boolean) => void;
};

const formatDetailRowLabel = ({
  intl,
  row,
}: {
  intl: IntlShape;
  row: PriceDetailRow;
}): string => {
  if (row.label.kind === "labelId") {
    return intl.formatMessage(
      labelMessages[row.label.id as keyof typeof labelMessages],
    );
  }

  return row.label.text;
};

/**
 * Renders a dynamic add-ons breakdown model via DS `PriceLineItem`, matching
 * the main `PriceSummarySections` binder. Pure renderer — the row/total shape
 * is decided by `buildAddOnsBreakdown`, not here. Consumers pass `intl`.
 *
 * Renders no heading: `model.titleId` belongs on the host tooltip's `title` so
 * it lands in the dialog/drawer header instead of the body.
 */
export const AddOnsDetailContent = ({
  currency,
  intl,
  model,
  onListOpenChange,
}: AddOnsDetailContentProps) => (
  <PriceSummary spacing={12} testId="add-on-price-summary">
    {model.rows.map((row) => {
      const label = formatDetailRowLabel({ intl, row });
      const itemDetails = row.itemDetails;

      return (
        <Fragment key={row.key}>
          {row.dividerBefore && <Divider spacing={0} />}
          <PriceLineItem
            caption={
              row.caption
                ? formatLabelRef({ currency, intl, ref: row.caption })
                : undefined
            }
            emphasis={row.emphasis}
            label={label}
            leftOffset={row.offset}
            middleContent={
              row.quantity != null ? `x${row.quantity}` : undefined
            }
            testId={`add-on-line-item-${row.key}`}
            tooltip={
              itemDetails
                ? {
                    content: (
                      <CloseListOnItemDetailsUnmount
                        onListOpenChange={onListOpenChange}
                      >
                        <AddOnItemDetails
                          currency={currency}
                          details={itemDetails}
                          hideHeading={true}
                          intl={intl}
                          name={label}
                        />
                      </CloseListOnItemDetailsUnmount>
                    ),
                    presentation: "modal",
                    title: label,
                  }
                : undefined
            }
            value={intl.formatNumber(row.amount, {
              style: "currency",
              currency,
            })}
          />
        </Fragment>
      );
    })}
  </PriceSummary>
);
